import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSellerData } from '../../../hooks/useSellerData';
import { useListingActions } from '../../../hooks/useListingActions';
import { useAuth } from '../../../context/AuthContext';
import Loading from '../../../components/common/Loading';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
// Import react-icons for exact matches
import { 
  LuChevronLeft,
  LuPlus,
  LuBox,
  LuRocket,
  LuEraser,
  LuCircle,
  LuRefreshCw,
  LuTrash2,
  LuChevronDown,
  LuChevronUp,
  LuEye,
  LuCalendar,
  LuFilter,
  LuMoreVertical,
  LuShoppingCart
} from 'react-icons/lu';

export default function AllListingsScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { listings, orders, loading, refreshing, fetchData } = useSellerData();
  const listingActions = useListingActions(fetchData);
  
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedListing, setExpandedListing] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [actionType, setActionType] = useState(''); // 'delete', 'sold', 'reactivate'

  // Filter listings based on active filter
  const filteredListings = listings.filter(listing => {
    if (activeFilter === 'all') return true;
    return listing.status === activeFilter;
  });

  // Stats for filter tabs
  const listingStats = {
    all: listings.length,
    active: listings.filter(l => l.status === 'active').length,
    sold: listings.filter(l => l.status === 'sold').length,
    draft: listings.filter(l => l.status === 'draft').length,
  };

  /* ------------------------------------------------------------------ */
  /*  NAVIGATION HANDLERS                                              */
  /* ------------------------------------------------------------------ */
  
  const navigateToCreateListing = () => {
    navigate('/store/listings/create-listing');
  };

  const navigateToBoostListing = (listing) => {
    navigate(`/store/boost-package/${listing.listing_id}`);
  };

  const navigateToPerformance = (listing) => {
    navigate(`/store/listings/performance/${listing.listing_id}`);
  };

  const navigateToListingDetails = (listing) => {
    navigate(`/store/listings/listing-details/${listing.listing_id}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  /* ------------------------------------------------------------------ */
  /*  LISTING ACTIONS                                                  */
  /* ------------------------------------------------------------------ */
  
  const confirmAction = (listing, type) => {
    setSelectedListing(listing);
    setActionType(type);
    setShowDeleteModal(true);
  };

  const handleConfirmAction = () => {
    if (!selectedListing) return;
    
    switch (actionType) {
      case 'delete':
        listingActions.handleDeleteListing(selectedListing.listing_id);
        break;
      case 'sold':
        listingActions.handleMarkAsSold(selectedListing.listing_id);
        break;
      case 'reactivate':
        listingActions.handleReactivate(selectedListing.listing_id);
        break;
      default:
        break;
    }
    
    setShowDeleteModal(false);
    setSelectedListing(null);
  };

  const getModalConfig = () => {
    if (!selectedListing) return {};
    
    switch (actionType) {
      case 'delete':
        return {
          title: 'Delete Listing',
          message: `Are you sure you want to permanently delete "${selectedListing.title}"? This action cannot be undone.`,
          confirmText: 'Delete Listing',
          confirmColor: 'bg-red-600 hover:bg-red-700'
        };
      case 'sold':
        return {
          title: 'Mark as Sold',
          message: `Are you sure you want to mark "${selectedListing.title}" as sold?`,
          confirmText: 'Mark as Sold',
          confirmColor: 'bg-green-600 hover:bg-green-700'
        };
      case 'reactivate':
        return {
          title: 'Reactivate Listing',
          message: `Would you like to reactivate "${selectedListing.title}" and make it available for sale again?`,
          confirmText: 'Reactivate',
          confirmColor: 'bg-orange-600 hover:bg-orange-700'
        };
      default:
        return {};
    }
  };

  /* ------------------------------------------------------------------ */
  /*  COMPONENTS                                                       */
  /* ------------------------------------------------------------------ */

  const FilterTabs = () => (
    <div className="flex overflow-x-auto bg-white border-b border-gray-200 px-4 lg:px-8 py-3 gap-2 scrollbar-hide">
      {[
        { key: 'all', label: 'All', emoji: '📦' },
        { key: 'active', label: 'Active', emoji: '🟢' },
        { key: 'sold', label: 'Sold', emoji: '🔴' },
        { key: 'draft', label: 'Draft', emoji: '🟡' },
      ].map((tab) => (
        <button
          key={tab.key}
          onClick={() => setActiveFilter(tab.key)}
          className={`
            flex items-center px-4 py-2 rounded-full border transition-colors whitespace-nowrap
            ${activeFilter === tab.key 
              ? 'bg-orange-600 border-orange-600 text-white' 
              : 'bg-gray-50 border-gray-300 text-gray-900 hover:bg-gray-100'
            }
          `}
        >
          <span className="mr-1.5">{tab.emoji}</span>
          <span className="font-medium text-sm">
            {tab.label} ({listingStats[tab.key]})
          </span>
        </button>
      ))}
    </div>
  );

  const ListingCard = ({ listing }) => {
    const listingOrders = orders.filter(o => o.listing_id === listing.listing_id);
    const isExpanded = expandedListing === listing.listing_id;

    const getStatusColor = (status) => {
      switch (status) {
        case 'active': return 'text-green-600 bg-green-50 border-green-200';
        case 'sold': return 'text-red-600 bg-red-50 border-red-200';
        case 'draft': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
        default: return 'text-gray-600 bg-gray-50 border-gray-200';
      }
    };

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4 hover:shadow-md transition-shadow">
        {/* Header */}
       
        <div className="flex items-center justify-between mb-3">
          <button 
            onClick={() => navigateToListingDetails(listing)}
            className="flex-1 text-left group"
          >
            <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors line-clamp-1">
              {listing.title}
            </h3>
            <div className="flex items-center gap-4 mt-1">
              <span className="text-lg font-bold text-orange-600">
                ₦{parseFloat(listing.price || 0).toLocaleString()}
              </span>
              <span className={`text-xs font-medium px-2 py-0.5 rounded border ${getStatusColor(listing.status)}`}>
                {listing.status === 'active' ? '🟢 Active' : 
                 listing.status === 'sold' ? '🔴 Sold' : '🟡 Draft'}
              </span>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <LuEye className="w-3 h-3" />
                  {listing.view_count || 0} views
                </span>
                <span className="flex items-center gap-1">
                  <LuCalendar className="w-3 h-3" />
                  {new Date(listing.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </button>
          <LuChevronDown className="w-5 h-5 text-gray-400" />
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2 mb-3">
          {listing.status === 'active' && (
            <>
              <button
                onClick={() => navigateToBoostListing(listing)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors text-sm font-medium"
              >
                <LuRocket className="w-3.5 h-3.5" />
                Boost
              </button>
              
              <button
                onClick={() => navigate(`/store/listings/edit-listing/${listing.listing_id}`)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
              >
                <LuEraser className="w-3.5 h-3.5" />
                Edit
              </button>
              
              <button
                onClick={() => confirmAction(listing, 'sold')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
              >
                <LuCircle className="w-3.5 h-3.5" />
                Mark as Sold
              </button>
            </>
          )}
          
          {listing.status === 'sold' && (
            <button
              onClick={() => confirmAction(listing, 'reactivate')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100 transition-colors text-sm font-medium"
            >
              <LuRefreshCw className="w-3.5 h-3.5" />
              Reactivate
            </button>
          )}
          
          <button
            onClick={() => confirmAction(listing, 'delete')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
          >
            <LuTrash2 className="w-3.5 h-3.5" />
            Delete
          </button>
        </div>

        {/* Orders Toggle */}
        {listingOrders.length > 0 && (
          <button
            onClick={() => setExpandedListing(isExpanded ? null : listing.listing_id)}
            className="w-full flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-orange-600">
              <LuShoppingCart className="w-4 h-4" />
              {listingOrders.length} Order{listingOrders.length > 1 ? 's' : ''}
            </span>
            {isExpanded ? (
              <LuChevronUp className="w-4 h-4 text-orange-600" />
            ) : (
              <LuChevronDown className="w-4 h-4 text-orange-600" />
            )}
          </button>
        )}

        {/* Expanded Orders */}
        {isExpanded && listingOrders.length > 0 && (
          <div className="mt-3 space-y-2">
            {listingOrders.slice(0, 3).map((order) => (
              <button
                key={order.order_id}
                onClick={() => navigate(`/store/order-details/${order.order_id}`)}
                className="w-full flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                <span className="font-medium text-gray-900">#{order.order_id}</span>
                <span className="text-gray-600">{order.buyer_first_name}</span>
                <span className="font-medium text-orange-600">
                  ₦{parseFloat(order.total_amount || 0).toLocaleString()}
                </span>
                <span className={`
                  px-2 py-0.5 rounded text-xs font-medium capitalize
                  ${order.status === 'completed' ? 'bg-green-100 text-green-800' :
                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    order.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'}
                `}>
                  {order.status}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <LuBox className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">
        {activeFilter === 'all' ? 'No Listings Yet' : `No ${activeFilter} Listings`}
      </h3>
      <p className="text-gray-600 mb-6 max-w-md">
        {activeFilter === 'all' 
          ? 'Create your first listing to start selling'
          : `You don't have any ${activeFilter} listings at the moment`
        }
      </p>
      {activeFilter === 'all' && (
        <button
          onClick={navigateToCreateListing}
          className="flex items-center gap-2 bg-orange-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
        >
          <LuPlus className="w-5 h-5" />
          Create First Listing
        </button>
      )}
    </div>
  );

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                           */
  /* ------------------------------------------------------------------ */
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loading size="large" />
          <p className="mt-4 text-gray-600">Loading your listings...</p>
        </div>
      </div>
    );
  }

  const modalConfig = getModalConfig();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LuChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-lg lg:text-xl font-bold text-gray-900">
              Manage your Listings
            </h1>
          </div>
          
          <button
            onClick={navigateToCreateListing}
            className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
          >
            <LuPlus className="w-5 h-5" />
            <span className="hidden sm:inline">Create Listing</span>
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <FilterTabs />

      {/* Main Content */}
      <div className="px-4 lg:px-8 py-6">
        {/* Desktop: Grid layout with filters sidebar */}
        <div className="lg:grid lg:grid-cols-4 lg:gap-6">
          {/* Desktop Filters Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 sticky top-24">
              <div className="flex items-center gap-2 mb-4">
                <LuFilter className="w-5 h-5 text-gray-600" />
                <h3 className="font-semibold text-gray-900">Filters</h3>
              </div>
              
              <div className="space-y-2">
                {[
                  { key: 'all', label: 'All Listings', count: listingStats.all },
                  { key: 'active', label: 'Active', count: listingStats.active },
                  { key: 'sold', label: 'Sold', count: listingStats.sold },
                  { key: 'draft', label: 'Draft', count: listingStats.draft },
                ].map((filter) => (
                  <button
                    key={filter.key}
                    onClick={() => setActiveFilter(filter.key)}
                    className={`w-full flex items-center justify-between p-2 rounded-lg transition-colors ${
                      activeFilter === filter.key
                        ? 'bg-orange-50 text-orange-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span className="font-medium">{filter.label}</span>
                    <span className="bg-gray-100 text-gray-900 text-xs font-medium px-2 py-1 rounded-full">
                      {filter.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Stats Summary */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Quick Stats</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Listings</span>
                    <span className="font-semibold">{listings.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Active</span>
                    <span className="font-semibold text-green-600">{listingStats.active}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total Orders</span>
                    <span className="font-semibold text-orange-600">{orders.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Listings Grid */}
          <div className="lg:col-span-3">
            {/* Mobile: Filter Summary */}
            <div className="lg:hidden mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-gray-600">Showing:</span>
                <span className="font-semibold capitalize">{activeFilter} ({filteredListings.length})</span>
              </div>
              <button
                onClick={() => {/* Implement filter dropdown */}}
                className="flex items-center gap-1 text-sm text-orange-600"
              >
                <LuFilter className="w-4 h-4" />
                Filter
              </button>
            </div>

            {/* Listings */}
            {filteredListings.length > 0 ? (
              <div>
                {filteredListings.map((listing) => (
                  <ListingCard key={listing.listing_id} listing={listing} />
                ))}
              </div>
            ) : (
              <EmptyState />
            )}

            {/* Desktop: Pagination (if needed) */}
            {filteredListings.length > 0 && (
              <div className="hidden lg:flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {Math.min(filteredListings.length, 10)} of {filteredListings.length} listings
                </div>
                <div className="flex gap-2">
                  <button className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                    Previous
                  </button>
                  <button className="px-3 py-1.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Floating Action Button */}
      <button
        onClick={navigateToCreateListing}
        className="lg:hidden fixed bottom-6 right-6 w-12 h-12 bg-orange-600 text-white rounded-full shadow-lg hover:bg-orange-700 transition-colors flex items-center justify-center z-50"
        aria-label="Create listing"
      >
        <LuPlus className="w-6 h-6" />
      </button>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedListing(null);
        }}
        onConfirm={handleConfirmAction}
        title={modalConfig.title || ''}
        message={modalConfig.message || ''}
        confirmText={modalConfig.confirmText || 'Confirm'}
        cancelText="Cancel"
        showCancel={true}
        confirmColor={modalConfig.confirmColor || 'bg-orange-600 hover:bg-orange-700'}
      />
      <Footer />
    </div>
  );
}